import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, PhoneCall, ShieldCheck, Bot, Layers, FileCheck2, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { SectionHeading, Reveal } from "../common/Section";
import { track, EVENTS } from "../../lib/analytics";

const OPTIONS = [
  { id: "chatbot", icon: MessageSquare, label: "Control a customer-facing AI chatbot", product: "Vör", to: "/vor",
    blurb: "Vör governs customer-facing AI: it controls what users can submit, what responses are released and when a human must step in." },
  { id: "calls", icon: PhoneCall, label: "Review regulated customer calls", product: "Lögberg", to: "/logberg",
    blurb: "Lögberg screens regulated conversations, prioritises material risks for human review and retains linked evidence." },
  { id: "employee", icon: ShieldCheck, label: "Stop employees sharing sensitive information with AI", product: "Skjold", to: "/skjold",
    blurb: "Skjold scans locally before anything is shared and blocks passports, bank statements, IDs and proof of address." },
  { id: "agents", icon: Bot, label: "Control AI agents and tool actions", product: "Enterprise AI", to: "/enterprise-ai",
    blurb: "Enterprise AI applies runtime control to copilots and agents — inspecting inputs, outputs and the actions they attempt to take." },
  { id: "visibility", icon: Layers, label: "Get visibility across multiple AI systems", product: "norse3 Platform", to: "/platform",
    blurb: "The norse3 platform applies one policy and one evidence trail across every AI system and channel you run." },
  { id: "evidence", icon: FileCheck2, label: "Evidence AI governance for compliance or the board", product: "Heimdall", to: "/how-it-works",
    blurb: "Heimdall centralises oversight and produces reviewable, time-stamped evidence for compliance teams and the board." },
];

export default function ProblemSelector() {
  const [active, setActive] = useState(OPTIONS[0].id);
  const sel = OPTIONS.find((o) => o.id === active);
  const choose = (id) => { setActive(id); track(EVENTS.PROBLEM_SELECTED, { problem: id }); };

  return (
    <section className="border-t border-border py-16 lg:py-24 bg-muted/40">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <SectionHeading eyebrow="Find your fit" title="What are you trying to control?" subtitle="Pick the closest problem and we'll point you to the right norse3 product." />
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
            {OPTIONS.map((o) => {
              const Icon = o.icon; const on = active === o.id;
              return (
                <button key={o.id} onClick={() => choose(o.id)} data-testid={`problem-${o.id}`}
                  className={`card-rise text-left rounded-lg border p-5 focus:outline-none focus:ring-2 focus:ring-primary/50 ${on ? "border-primary/40 bg-card shadow-md shadow-primary/10" : "border-border bg-card hover:border-primary/40 hover:shadow-sm"}`}>
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${on ? "bg-primary text-white" : "bg-primary/10 text-primary"}`}><Icon className="h-5 w-5" /></span>
                  <p className="mt-3.5 text-sm font-medium text-foreground">{o.label}</p>
                </button>
              );
            })}
          </div>
          <div className="lg:sticky lg:top-24 rounded-lg border border-primary/25 bg-gradient-to-b from-primary/[0.06] to-background p-7 shadow-sm" data-testid="problem-result">
            <p className="text-xs uppercase tracking-wide text-primary font-semibold">Recommended</p>
            <p className="mt-1 font-heading text-2xl font-bold text-foreground">norse3 {sel.product}</p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{sel.blurb}</p>
            <Button asChild className="mt-5 w-full bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to={sel.to} onClick={() => track(EVENTS.PRODUCT_SELECTED, { product: sel.product, from: "problem_selector" })}>
                Explore {sel.product} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
